import React, { PropsWithChildren, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { Button, Col, Grid, Row } from 'rsuite';
import { convertNumberToCurrency } from '../../../../common/utils';
import Notice from '../../../user-feedback/Notice';
import usePlaceNavigate from '../../../../hooks/usePlaceNavigate';
import { setPaymentFormData } from '../../../../store/slices/payment/paymentSlice';

export interface CallbackResult {
  formData?: {
    captureContext: string;
  };
  url: string | URL;
  id?: string;
  place?: string;
}

interface RedirectBasedPaymentProps {
  onClickCallback: () => Promise<CallbackResult>;
  loading?: boolean;
  launchWindow?: boolean;
  totalDue?: string | number;
  error?: string;
  dataTestId?: string;
}

export const RedirectBasedPayment = ({
  children,
  launchWindow,
  onClickCallback,
  loading,
  totalDue,
  error,
  dataTestId,
}: PropsWithChildren<RedirectBasedPaymentProps>) => {
  const dispatch = useDispatch();
  const navigate = usePlaceNavigate(true);
  const handleButtonClick = useCallback(async () => {
    try {
      const { url, formData } = await onClickCallback();
      if (
        (typeof url === 'string' && url.startsWith('http')) ||
        url instanceof URL
      ) {
        if (launchWindow) {
          window.open(url, '_blank', 'toolbar=0,location=0,menubar=0');
        }
      } else {
        if (formData) {
          dispatch(setPaymentFormData(formData));
        }
        if (launchWindow) {
          navigate(url as string);
        }
      }
    } catch (err) {
      console.error('Error loading the data', err);
    }
  }, [onClickCallback, launchWindow, navigate, dispatch]);

  return (
    <Grid
      fluid
      data-testid={dataTestId}
    >
      <Row className="mb-16">
        <Col xs={24}>
          {children || (
            <>
              <p>
                A new browser window has been opened which will allow you to
                complete payment through and external payment provider.
              </p>
              <p>
                Please continue with your payment in this new browser window.
              </p>
              <p>
                Once your payment is completed please return to this browser
                window.
              </p>
            </>
          )}
        </Col>
      </Row>
      {error && (
        <Row className="mb-16">
          <Col xs={24}>
            <Notice message={error} />
          </Col>
        </Row>
      )}
      <Row>
        <Col xs={24}>
          <Button
            className="btn-payment btn-soft-blue"
            onClick={handleButtonClick}
            disabled={loading}
          >
            Pay {totalDue && `${convertNumberToCurrency(totalDue)}`}
          </Button>
        </Col>
      </Row>
    </Grid>
  );
};
